import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import EmailEditModal from "./EmailEditModal";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  FileText, 
  Download,
  Eye,
  X,
  Save,
  Loader2,
  Calendar,
  Package,
  Building2,
  Copy,
  CheckCircle,
  Check,
  Clock,
  XCircle,
  Send,
  Upload,
  DollarSign
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-slate-500', icon: Clock },
  sent: { label: 'Enviada', color: 'bg-blue-500', icon: Send },
  received: { label: 'Recibida', color: 'bg-green-500', icon: CheckCircle },
  cancelled: { label: 'Cancelada', color: 'bg-red-500', icon: XCircle }
};

// Purchase Order Form Component
const PurchaseOrderForm = ({ order, suppliers, authorizers, businessId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    supplier_id: '',
    date: new Date().toISOString().split('T')[0],
    items: [],
    terms_conditions: `Precios en MXN (Pesos Mexicanos).
Tiempo de entrega 2 días hábiles después de generar el pedido.
Forma de pago: Contado anticipado mediante transferencia electrónica.`,
    authorized_user_id: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [searchingItem, setSearchingItem] = useState(null); // index of item being searched
  const [searchText, setSearchText] = useState('');

  // Load vehicles from DB for autocomplete
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const res = await axios.get(`${API}/vehicles?business_id=${businessId}`);
        if (res.data.success) {
          setVehicles(res.data.vehicles || []);
        }
      } catch (e) {
        console.error('Error loading vehicles:', e);
      }
    };
    loadVehicles();
  }, [businessId]);

  useEffect(() => {
    if (order) {
      setFormData({
        supplier_id: order.supplier_id || '',
        date: order.date ? new Date(order.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        items: order.items || [],
        terms_conditions: order.terms_conditions || '',
        authorized_user_id: order.authorized_user_id || '',
        notes: order.notes || ''
      });
    }
  }, [order]);

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || 0)), 0);
  const ivaAmount = subtotal * 0.16;
  const total = subtotal + ivaAmount;

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        item_id: Date.now().toString(),
        code: '',
        concept: '',
        unit: 'PIEZA',
        quantity: 1,
        unit_price: 0,
        subtotal: 0
      }]
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        updated.subtotal = (updated.quantity || 1) * (updated.unit_price || 0);
        return updated;
      })
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Select vehicle from autocomplete
  const selectVehicle = (index, vehicle) => {
    const concept = `${vehicle.brand || ''} ${vehicle.model_name || ''} ${vehicle.version || ''} ${vehicle.year || ''}`.trim();
    // El purchase_price YA incluye IVA, así que desglosamos para obtener el precio sin IVA
    const priceWithIva = parseFloat(vehicle.purchase_price || vehicle.price || 0);
    const priceWithoutIva = Math.round((priceWithIva / 1.16) * 100) / 100;
    updateItem(index, 'concept', concept);
    // Small delay to update price after concept
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map((item, i) => {
          if (i !== index) return item;
          const updated = { ...item, concept: concept, unit_price: priceWithoutIva, unit: 'UNIDAD' };
          updated.subtotal = (updated.quantity || 1) * priceWithoutIva;
          return updated;
        })
      }));
    }, 0);
    setSearchingItem(null);
    setSearchText('');
  };

  // Filter vehicles by search text
  const getFilteredVehicles = () => {
    if (!searchText || searchText.length < 1) return vehicles;
    const lower = searchText.toLowerCase();
    return vehicles.filter(v => {
      const full = `${v.brand || ''} ${v.model_name || ''} ${v.version || ''} ${v.year || ''}`.toLowerCase();
      return full.includes(lower);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      toast.error("Selecciona un proveedor");
      return;
    }
    if (formData.items.length === 0) {
      toast.error("Agrega al menos un artículo");
      return;
    }
    if (!formData.authorized_user_id) {
      toast.error("Selecciona quién autoriza la orden");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        business_id: businessId,
        ...formData
      };

      if (order?.id) {
        await axios.put(`${API}/purchase-orders/${order.id}`, payload);
        toast.success("Orden de compra actualizada");
      } else {
        await axios.post(`${API}/purchase-orders`, payload);
        toast.success("Orden de compra creada");
      }
      onSave();
    } catch (error) {
      console.error('Error saving purchase order:', error);
      toast.error("Error al guardar orden de compra");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-500" />
          {order?.id ? `Editar Orden ${order.order_number}` : 'Nueva Orden de Compra'}
        </h3>
        <button onClick={onCancel} className="p-2 text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Proveedor y Fecha */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Proveedor *</label>
            <select
              value={formData.supplier_id}
              onChange={(e) => setFormData({...formData, supplier_id: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
            >
              <option value="">Seleccionar proveedor...</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.business_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Fecha</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Items */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Artículos</h4>
            <button
              type="button"
              onClick={addItem}
              className="px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Agregar
            </button>
          </div>

          {formData.items.length === 0 ? (
            <div className="text-center py-8 text-slate-500 border border-dashed border-slate-700 rounded-lg">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No hay artículos. Haz clic en "Agregar" para comenzar.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 font-medium px-2">
                <div className="col-span-2">CLAVE</div>
                <div className="col-span-4">CONCEPTO</div>
                <div className="col-span-1">UNIDAD</div>
                <div className="col-span-1">CANT</div>
                <div className="col-span-2">P.U.</div>
                <div className="col-span-1">TOTAL</div>
                <div className="col-span-1"></div>
              </div>
              
              {formData.items.map((item, index) => (
                <div key={item.item_id} className="grid grid-cols-12 gap-2 items-center bg-slate-800/30 rounded-lg p-2">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={item.code}
                      onChange={(e) => updateItem(index, 'code', e.target.value.toUpperCase())}
                      placeholder="CLV"
                      className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm"
                    />
                  </div>
                  <div className="col-span-4 relative">
                    <input
                      type="text"
                      value={item.concept}
                      onChange={(e) => {
                        updateItem(index, 'concept', e.target.value);
                        setSearchText(e.target.value);
                        setSearchingItem(index);
                      }}
                      onFocus={() => { setSearchingItem(index); setSearchText(item.concept || ''); }}
                      onBlur={() => setTimeout(() => setSearchingItem(null), 200)}
                      placeholder="Buscar vehículo o escribir concepto..."
                      className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm"
                      data-testid={`item-concept-${index}`}
                    />
                    {searchingItem === index && vehicles.length > 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                        {getFilteredVehicles().map((v, vi) => (
                          <button
                            key={v.id || vi}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); selectVehicle(index, v); }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-700 flex items-center justify-between text-sm border-b border-slate-700/50 last:border-0"
                            data-testid={`vehicle-option-${vi}`}
                          >
                            <div>
                              <span className="text-white font-medium">{v.brand} {v.model_name}</span>
                              <span className="text-slate-400 ml-1">{v.version}</span>
                              {v.year && <span className="text-blue-400 ml-1">{v.year}</span>}
                            </div>
                            {(v.purchase_price || v.price) && (
                              <span className="text-green-400 text-xs ml-2 flex-shrink-0">
                                {formatCurrency(parseFloat(v.purchase_price || v.price || 0))}
                              </span>
                            )}
                          </button>
                        ))}
                        {getFilteredVehicles().length === 0 && (
                          <div className="px-3 py-2 text-slate-500 text-xs">No se encontraron vehículos</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-1">
                    <input
                      type="text"
                      value={item.unit}
                      onChange={(e) => updateItem(index, 'unit', e.target.value.toUpperCase())}
                      className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm text-center"
                    />
                  </div>
                  <div className="col-span-1">
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      min="1"
                      className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm text-center"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm text-right"
                    />
                  </div>
                  <div className="col-span-1 text-right text-white text-sm font-medium">
                    {formatCurrency(item.subtotal || 0)}
                  </div>
                  <div className="col-span-1 text-right">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-72 space-y-2">
            <div className="flex justify-between text-slate-400">
              <span>Subtotal:</span>
              <span className="text-white">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-slate-400">
              <span>IVA (16%):</span>
              <span className="text-white">{formatCurrency(ivaAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700">
              <span className="text-white">Total:</span>
              <span className="text-orange-400">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        {/* Autorización */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Autoriza *</label>
          <select
            value={formData.authorized_user_id}
            onChange={(e) => setFormData({...formData, authorized_user_id: e.target.value})}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
          >
            <option value="">Seleccionar quién autoriza...</option>
            {authorizers.map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.username} - {u.position || 'Sin cargo'}</option>
            ))}
          </select>
          {authorizers.length === 0 && (
            <p className="text-xs text-orange-400 mt-1">
              No hay usuarios autorizados. Configura permisos en la sección de Usuarios.
            </p>
          )}
        </div>

        {/* Términos */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Términos y Condiciones</label>
          <textarea
            value={formData.terms_conditions}
            onChange={(e) => setFormData({...formData, terms_conditions: e.target.value})}
            rows={4}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none resize-none"
          />
        </div>

        {/* Notas */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Notas (internas)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            rows={2}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none resize-none"
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {order?.id ? 'Actualizar' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Order Card Component
const OrderCard = ({ order, onEdit, onDelete, onDownloadPdf, onSendEmail, onUploadPayment, onSendPayment }) => {
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  const hasPayment = !!order.payment_receipt;

  return (
    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 hover:bg-slate-800/50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <FileText className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-white">{order.order_number}</h4>
              <span className={`px-2 py-0.5 rounded text-xs text-white flex items-center gap-1 ${status.color}`}>
                <StatusIcon className="w-3 h-3" />
                {status.label}
              </span>
              {order.sent_to_email && (
                <span className="text-xs text-green-400">OC enviada</span>
              )}
              {hasPayment && (
                <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400">Pago registrado</span>
              )}
              {order.source_quotation_number && (
                <span
                  className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-300"
                  title={`Generada desde cotización ${order.source_quotation_number}`}
                >
                  ← {order.source_quotation_number}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400">
              {order.supplier_name || <span className="text-amber-400 italic">Sin proveedor asignado</span>}
            </p>
            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(order.date)}
              </span>
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                {order.items?.length || 0} items
              </span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-orange-400">{formatCurrency(order.total)}</p>
          <div className="flex items-center gap-1 mt-2">
            <button onClick={() => onSendEmail(order)} className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors" title="Enviar OC por correo">
              <Send className="w-4 h-4" />
            </button>
            <button onClick={() => hasPayment ? onSendPayment(order) : onUploadPayment(order)}
              className={`p-1.5 rounded transition-colors ${hasPayment ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-amber-400 hover:bg-amber-500/20'}`}
              title={hasPayment ? "Enviar comprobante de pago" : "Subir comprobante de pago"}>
              <DollarSign className="w-4 h-4" />
            </button>
            <button onClick={() => onDownloadPdf(order)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded transition-colors" title="Descargar PDF">
              <Download className="w-4 h-4" />
            </button>
            <button onClick={() => onEdit(order)} className="p-1.5 text-slate-400 hover:bg-slate-700 rounded transition-colors" title="Editar">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete(order)} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors" title="Eliminar">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function PurchaseOrdersTab({ businessId }) {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [authorizers, setAuthorizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  const fetchData = async () => {
    try {
      const [ordersRes, suppliersRes, authRes] = await Promise.all([
        axios.get(`${API}/purchase-orders?business_id=${businessId}`),
        axios.get(`${API}/suppliers?business_id=${businessId}`),
        axios.get(`${API}/users/authorizers?business_id=${businessId}`)
      ]);
      setOrders(ordersRes.data.orders || []);
      setSuppliers(suppliersRes.data.suppliers || []);
      setAuthorizers(authRes.data.users || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [businessId]);

  const handleEdit = (order) => {
    setEditingOrder(order);
    setShowForm(true);
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`¿Eliminar orden "${order.order_number}"?`)) return;
    
    try {
      await axios.delete(`${API}/purchase-orders/${order.id}`);
      toast.success("Orden eliminada");
      fetchData();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error("Error al eliminar orden");
    }
  };

  const handleDownloadPdf = async (order) => {
    try {
      toast.info("Generando PDF...");
      const response = await axios.get(`${API}/purchase-orders/${order.id}/pdf`);
      if (!response.data.success || !response.data.pdf) {
        toast.error("Error al generar PDF");
        return;
      }
      
      // Convertir base64 a blob
      const byteChars = atob(response.data.pdf);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArr], { type: 'application/pdf' });
      const filename = response.data.filename || `OC-${order.order_number}.pdf`;
      
      // Usar File System Access API — abre el cuadro nativo "Guardar como..."
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success("PDF guardado correctamente");
          return;
        } catch (e) {
          if (e.name === 'AbortError') return; // Usuario canceló
        }
      }
      
      // Fallback para navegadores sin showSaveFilePicker
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success("PDF descargado");
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error("Error al descargar PDF");
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingOrder(null);
    fetchData();
  };

  const [sendingEmail, setSendingEmail] = useState(null); // order with supplier email
  
  const handleSendEmail = async (order) => {
    // Get supplier email before showing confirmation
    try {
      const supplierData = suppliers.find(s => s.id === order.supplier_id);
      const email = supplierData?.primary_contact?.email || supplierData?.email || "";
      setSendingEmail({ ...order, _supplier_email: email });
    } catch (e) {
      setSendingEmail({ ...order, _supplier_email: "" });
    }
  };

  const confirmSendEmail = async () => {
    if (!sendingEmail) return;
    const order = sendingEmail;
    const overrideEmail = sendingEmail._supplier_email;
    setSendingEmail(null);
    try {
      toast.info(`Enviando OC ${order.order_number}...`);
      const response = await axios.post(`${API}/purchase-orders/${order.id}/send-email`, {
        override_email: overrideEmail || ""
      });
      if (response.data.success) {
        toast.success(`Correo enviado a ${response.data.sent_to}`);
        fetchData();
      } else {
        toast.error(response.data.error || "Error al enviar correo");
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error("Error: " + (error.response?.data?.detail || error.message));
    }
  };

  // ==================== PAYMENT RECEIPT ====================
  const [paymentOrder, setPaymentOrder] = useState(null); // Order for upload modal
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null); // Extracted data after upload
  const [sendingPayment, setSendingPayment] = useState(null); // Order for send payment email modal
  const [paymentEmail, setPaymentEmail] = useState('');
  const paymentInputRef = useRef(null);

  const handleUploadPayment = (order) => {
    setPaymentOrder(order);
    setPaymentData(null);
  };

  const handlePaymentFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !paymentOrder) return;
    setUploadingPayment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/purchase-orders/${paymentOrder.id}/upload-payment`, formData, { timeout: 60000 });
      if (res.data.success) {
        setPaymentData(res.data.payment);
        toast.success("Comprobante subido y datos extraídos");
        fetchData();
      } else {
        toast.error(res.data.error || "Error al procesar comprobante");
      }
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingPayment(false);
      if (paymentInputRef.current) paymentInputRef.current.value = '';
    }
  };

  const handleSendPayment = (order) => {
    const supplierData = suppliers.find(s => s.id === order.supplier_id);
    const email = supplierData?.primary_contact?.email || supplierData?.email || "";
    setPaymentEmail(email);
    setSendingPayment(order);
  };

  const confirmSendPayment = async () => {
    if (!sendingPayment) return;
    const order = sendingPayment;
    setSendingPayment(null);
    try {
      toast.info("Enviando comprobante de pago...");
      const res = await axios.post(`${API}/purchase-orders/${order.id}/send-payment-email`, {
        override_email: paymentEmail
      });
      if (res.data.success) {
        toast.success(`Comprobante enviado a ${res.data.sent_to}`);
        fetchData();
      } else {
        toast.error(res.data.error || "Error al enviar");
      }
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    }
  };

  const filteredOrders = orders.filter(o => 
    o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
    o.supplier_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (showForm) {
    return (
      <PurchaseOrderForm
        order={editingOrder}
        suppliers={suppliers}
        authorizers={authorizers}
        businessId={businessId}
        onSave={handleSave}
        onCancel={() => { setShowForm(false); setEditingOrder(null); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Modal de confirmación de envío (OC) */}
      <EmailEditModal
        open={!!sendingEmail}
        onClose={() => setSendingEmail(null)}
        onSent={() => fetchData()}
        title={sendingEmail ? `Enviar Orden ${sendingEmail.order_number}` : 'Enviar Orden'}
        sendEndpoint={sendingEmail ? `/api/purchase-orders/${sendingEmail.id}/send-email` : ''}
        initialTo={sendingEmail?._supplier_email || ''}
        confirmLabel="Enviar Orden"
        helperText="Se adjuntará el PDF de la orden de compra. Puedes editar destinatario, asunto y cuerpo."
      />

      {/* Modal de subir comprobante de pago */}
      {paymentOrder && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => { setPaymentOrder(null); setPaymentData(null); }}>
          <div className="bg-slate-800 rounded-xl border border-slate-600 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Subir Comprobante de Pago
            </h3>
            <p className="text-slate-400 text-sm mb-2">Orden: <span className="text-white font-medium">{paymentOrder.order_number}</span> — {paymentOrder.supplier_name}</p>
            
            {!paymentData ? (
              <div>
                <label className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-slate-600 rounded-lg hover:border-emerald-500 cursor-pointer transition-colors mt-4">
                  {uploadingPayment ? (
                    <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                  ) : (
                    <Upload className="w-6 h-6 text-slate-400" />
                  )}
                  <div>
                    <p className="text-white text-sm font-medium">{uploadingPayment ? "Procesando..." : "Seleccionar comprobante"}</p>
                    <p className="text-slate-400 text-xs">PDF o imagen del comprobante bancario</p>
                  </div>
                  <input ref={paymentInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handlePaymentFileChange} className="hidden" disabled={uploadingPayment} />
                </label>
                <button onClick={() => { setPaymentOrder(null); setPaymentData(null); }} className="w-full mt-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">Cancelar</button>
              </div>
            ) : (
              <div>
                <p className="text-green-400 text-sm mb-3 flex items-center gap-1"><Check className="w-4 h-4" /> Datos extraídos del comprobante:</p>
                <div className="space-y-2 text-sm bg-slate-900 rounded-lg p-3 mb-4">
                  <div className="flex justify-between"><span className="text-slate-400">Monto:</span><span className="text-white font-bold">${paymentData.extracted?.amount || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Concepto:</span><span className="text-white">{paymentData.extracted?.concept || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Fecha:</span><span className="text-white">{paymentData.extracted?.date || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Referencia:</span><span className="text-white">{paymentData.extracted?.reference || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Beneficiario:</span><span className="text-white">{paymentData.extracted?.beneficiary_name || "—"}</span></div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { setPaymentOrder(null); setPaymentData(null); }} className="flex-1 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600">Cerrar</button>
                  <button onClick={() => { setPaymentOrder(null); setPaymentData(null); handleSendPayment(paymentOrder); }}
                    className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 font-medium flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" /> Enviar al Proveedor
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de enviar comprobante de pago */}
      <EmailEditModal
        open={!!sendingPayment}
        onClose={() => setSendingPayment(null)}
        onSent={() => fetchData()}
        title={sendingPayment ? `Comprobante de Pago — ${sendingPayment.order_number}` : 'Enviar Comprobante'}
        sendEndpoint={sendingPayment ? `/api/purchase-orders/${sendingPayment.id}/send-payment-email` : ''}
        initialTo={paymentEmail || ''}
        confirmLabel="Enviar Comprobante"
        helperText="Se adjuntará el comprobante de pago. Puedes editar destinatario, asunto y cuerpo."
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Órdenes de Compra</h2>
          <p className="text-slate-400 text-sm">{orders.length} órdenes</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
          data-testid="new-po-btn"
        >
          <Plus className="w-4 h-4" />
          Nueva Orden
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          placeholder="Buscar por número o proveedor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">
            {search ? 'No se encontraron órdenes' : 'No hay órdenes de compra'}
          </p>
          {!search && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-orange-400 hover:text-orange-300"
            >
              Crear primera orden
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDownloadPdf={handleDownloadPdf}
              onSendEmail={handleSendEmail}
              onUploadPayment={handleUploadPayment}
              onSendPayment={handleSendPayment}
            />
          ))}
        </div>
      )}
    </div>
  );
}
